<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  PlatformType,
  PlatformPayloadType,
} from "@/common/types/platform";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: PlatformType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  code: "",
  status: "ACTIVE",
  description: "",
};

const payload = ref<PlatformPayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, code, status, description } = props.data;

  payload.value = {
    ...payload.value,
    code,
    name,
    status,
    description,
  };
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toUpperCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-platform">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('platform.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("platform.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.code"
      :value="payload.code"
      :placeholder="t('platform.placeholders.code')"
      :mask="maskCode"
      required
      @update:modelValue="debouceUpdate('code')"
    >
      <template #label> {{ t("platform.labels.code") }} * </template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionsStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }}</template>
    </base-select>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("platform.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-platform {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
